import {Component} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faBan,
  faCircleInfo,
  faFileExcel,
  faFire,
  faMagnifyingGlass,
  faPen,
  faPenToSquare,
  faSquarePlus,
  faTrash,
  faUpload,
  faWarning
} from '@fortawesome/free-solid-svg-icons';
import {ApiService} from '../services/api.service';
import {ToastrService} from 'ngx-toastr';
import {getFileNameFromResponse} from '../../common/utils';
import saveAs from 'file-saver';
import {GenericSearchComponent} from '../shared/search-suggestion/search-suggestion.component';
import {VehicleNumberCopyDirective} from '../shared/vehicle-number-copy/vehicle-number-copy.directive';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../shared/constants/pagination.constants';


interface VehicleRecord {
  sNo: number;
  empId: string;
  vehicleNo: string;
  personName: string;
  addedBy: string;
  createdOn: string;
}

@Component({
    selector: 'app-vehicle-database',
    templateUrl: './vehicle-database.component.html',
    styleUrls: ['./vehicle-database.component.css'],
    imports: [CommonModule, FormsModule, FontAwesomeModule, GenericSearchComponent, VehicleNumberCopyDirective]
})
export class VehicleDatabaseComponent {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
  searchText: string  = '';
  recordsPerPage: number = 10;
  currentPage: number = 1;
  vehicleDatabase:any = []; 
  nextPage: number | null = null;
  previousPage: number | null = null;
  limit: number = 10;
  showConfirmationDialog: boolean = false;
  pId: number = 0;
  totalRecords:number = 0
  todayDate: Date = new Date();
  
  // New properties for action tracking
  confirmationTitle: string = '';
  confirmationMessage: string = '';
  currentActionType: 'delete' | 'blacklist' | 'hotlist' = 'delete';
  selectedVehicle: any = null;
  constructor(public apiService: ApiService,
    library: FaIconLibrary,
    private router: Router,
    private toastr:ToastrService) {
    this.fetchData(this.currentPage, this.recordsPerPage);
    library.addIcons(faTrash,
                    faFileExcel,
                    faAngleRight,
                    faAngleLeft,
                    faUpload,
                    faSquarePlus,
                    faMagnifyingGlass,
                    faPen,
                    faPenToSquare,
                    faWarning,
                    faCircleInfo,
                    faBan,
                    faFire
                  );
  }

  addVehicle() {
    this.router.navigate(['/add-vehicle']);
  }

  bulkUpload() {
    this.router.navigate(['/vehicle/bulk'])
  }

    downloadTemplate() {
        this.apiService.downloadImportTemplate('vehicle').subscribe({
            next: (response) => {
                const fileName = getFileNameFromResponse(response);
                if (response.body) {
                    saveAs(response.body, fileName);
                }
            },
            error: (err) => {
                if (err?.error?.detail) {
                    this.toastr.error(err.error.detail, 'Error');
                } else if (err?.error?.error) {
                    this.toastr.error(err.error.error, 'Error');
                }
            },
        });
    }


  fetchData(page: number, pageSize: number){
    this.apiService.getVehicleDatabase(page, pageSize, this.searchText).subscribe(data => {
      this.vehicleDatabase = data['results'];
      this.totalRecords = data['count'];
      this.nextPage = data['next']
      this.previousPage = data['previous']
    });
  }
  get paginatedRecords(): VehicleRecord[] {
    return []
  }
  get totalPages(): number {
      let totalPage = Math.ceil(this.totalRecords / this.recordsPerPage);
      if (totalPage === 0){
        totalPage = 1;
      }
      return totalPage;
    }

  deleteRecord(index: number) {}
  next() {
    if (this.nextPage){
      this.currentPage++;
      this.fetchData(this.currentPage, this.recordsPerPage);
    }
  }

  previous() {
    if (this.previousPage){
      this.currentPage--;
      this.fetchData(this.currentPage, this.recordsPerPage);
      }
  }

  selectPageSize(){
    if(this.recordsPerPage){
      this.currentPage = 1;
      this.fetchData(this.currentPage, this.recordsPerPage);
    }
  }
  // Model for confirming deletion
  openConfirmationDialog(index: number) {
    this.currentActionType = 'delete';
    this.pId = index;
    this.confirmationTitle = 'Are You Sure?';
    this.confirmationMessage = 'Do you want to permanently delete this vehicle? Confirm your choice below!';
    this.showConfirmationDialog = true;
  }

  openBlacklistConfirmationDialog(vehicle: any) {
    this.currentActionType = 'blacklist';
    this.selectedVehicle = vehicle;
    this.confirmationTitle = 'Blacklist Vehicle?';
    this.confirmationMessage = `Do you want to add vehicle ${vehicle.vehicle_number} to the blacklist? This action will prevent the vehicle from being tracked.`;
    this.showConfirmationDialog = true;
  }

  openHotlistConfirmationDialog(vehicle: any) {
    this.currentActionType = 'hotlist';
    this.selectedVehicle = vehicle;
      this.confirmationTitle = 'Hotlisted Vehicle?';
      this.confirmationMessage = `Do you want to add vehicle ${vehicle.vehicle_number} to Hotlisted Vehicles? This action will flag the vehicle as urgent to monitor.`;
    this.showConfirmationDialog = true;
  }

  closeConfirmationDialog() {
    this.showConfirmationDialog = false;
  }

  confirmAction() {
    switch (this.currentActionType) {
      case 'delete':
        this.confirmDelete();
        break;
      case 'blacklist':
        this.confirmBlacklist();
        break;
      case 'hotlist':
        this.confirmHotlist();
        break;
    }
  }

  private confirmDelete() {
    this.apiService.deleteVehicle(this.pId).subscribe({
      next: (response:any) => {
        this.toastr.success('Vehicle deleted successfully.', 'Success');
        this.closeConfirmationDialog();
        // this.router.navigate(['/vehicle']);
        window.location.reload();
      },
      error: (err) => {
        if (err['error']['detail']) {
            this.toastr.error(err['error']['detail'], 'Error');
          
        }  else if (err['status'] !== 403 && err.status !== 0) {
            this.toastr.error("Something went wrong!", 'Error');
        }
        this.closeConfirmationDialog();
      }
    })
  }

  private confirmBlacklist() {
    const formdata = new FormData();
    formdata.append('vehicle_number', this.selectedVehicle.vehicle_number);
    if (this.selectedVehicle.person_name) {
      formdata.append('person_name', this.selectedVehicle.person_name);
    }
    formdata.append('reason', 'Added from vehicle database');
    formdata.append('is_active', 'true');

    this.apiService.addBlackListedVehicle(formdata).subscribe({
      next: (response:any) => {
        this.toastr.success(`Vehicle ${this.selectedVehicle.vehicle_number} has been blacklisted successfully.`, 'Success');
        this.closeConfirmationDialog();
        window.location.reload();
      },
      error: (err) => {
        if (err['error']['non_field_errors']) {
          err['error']['non_field_errors'].forEach((element:any) => {
            this.toastr.error(element, 'Error');
          });
        } else if (err['error']['vehicle_number']) {
          err['error']['vehicle_number'].forEach((element:any) => {
            this.toastr.error(element, 'Error');
          });
        } else if (err['status'] !== 403 && err.status !== 0) {
          this.toastr.error("Something went wrong!", 'Error');
        }
        this.closeConfirmationDialog();
      }
    });
  }

  private confirmHotlist() {
    const formdata = new FormData();
    formdata.append('vehicle_number', this.selectedVehicle.vehicle_number);
    if (this.selectedVehicle.person_name) {
      formdata.append('person_name', this.selectedVehicle.person_name);
    }
    formdata.append('reason', 'Added from vehicle database');
    formdata.append('is_active', 'true');

    this.apiService.addHotList(formdata).subscribe({
      next: (response:any) => {
          this.toastr.success(`Vehicle ${this.selectedVehicle.vehicle_number} has been added to Hotlisted Vehicles successfully.`, 'Success');
        this.closeConfirmationDialog();
        window.location.reload();
      },
      error: (err) => {
        if (err['error']['non_field_errors']) {
          err['error']['non_field_errors'].forEach((element:any) => {
            this.toastr.error(element, 'Error');
          });
        } else if (err['error']['vehicle_number']) {
          err['error']['vehicle_number'].forEach((element:any) => {
            this.toastr.error(element, 'Error');
          });
        } else if (err['status'] !== 403 && err.status !== 0) {
          this.toastr.error("Something went wrong!", 'Error');
        }
        this.closeConfirmationDialog();
      }
    });
  }

  updateVehicle(pk: number){
    return this.router.navigate([`edit-vehicle/${pk}`])
  }
  
  vehicleSearch(){
    this.currentPage = 1;
    this.fetchData(this.currentPage, this.recordsPerPage);
  }

  exportToExcel() {
    this.apiService.exportVehicleDB(this.currentPage, this.recordsPerPage, this.searchText).subscribe({
      next: (response: any) => {
        const fileName = getFileNameFromResponse(response);
          if (response.body) {
              saveAs(response.body, fileName);
          }
       
      },
      error: (err) => {
        this.toastr.error(`${err}`, 'Error')
      }
    })
  }

onResults(event: { searchText: string, results: any[], totalRecords: number, nextPage: number | null, previousPage: number | null }) {
  this.currentPage = 1;
  this.recordsPerPage = 10;
  this.previousPage = event.previousPage;
  this.nextPage = event.nextPage;
  this.searchText = event.searchText;
  this.vehicleDatabase = event.results;
  this.totalRecords = event.totalRecords;
}


}
